import { MessageCircle, Plus, Sparkles, ShoppingCart } from 'lucide-react';
import {
  addOnNameLooksDrinkLike,
  productIncludedHasDrink,
  productIncludedHasFries,
  productIsBurgerish,
} from '@/lib/productComposition';
import { pickCrossSellProducts } from '@/lib/upsell/pickCrossSellProducts';

function idKey(x) {
  return x == null || x === '' ? '' : String(x);
}

/**
 * @param {object} addedProduct
 * @param {object} ctx
 * @param {object|null} ctx.menuData
 * @param {() => any[]} ctx.getCartLinesForUpsell
 */
export function pickUpsellProducts(addedProduct, ctx) {
  return pickCrossSellProducts(addedProduct, {
    menuData: ctx.menuData,
    cartLines: ctx.getCartLinesForUpsell?.() || [],
    limit: 2,
  });
}

export function buildPostAddAddonCardRows(product, cartLineId) {
  const raw = product?.add_ons || [];
  const rows = raw
    .map((a, i) => {
      const name = String(a?.name ?? '').trim();
      if (!name) return null;
      return {
        productId: product.id,
        addonIndex: i,
        name,
        price: Number(a?.price) || 0,
        drinkLike: addOnNameLooksDrinkLike(name),
      };
    })
    .filter(Boolean);
  const hasDrinkIncluded = productIncludedHasDrink(product);
  if (!hasDrinkIncluded) {
    rows.sort((a, b) => (b.drinkLike ? 1 : 0) - (a.drinkLike ? 1 : 0));
  }
  const lineKey = cartLineId != null && String(cartLineId) !== '' ? String(cartLineId) : undefined;
  return rows.map(({ drinkLike, ...rest }) => ({
    ...rest,
    ...(lineKey ? { cartLineId: lineKey } : {}),
  }));
}

export function buildPostAddUpsellHook(addedProduct, addonRows, externalUpsell) {
  const hasFries = productIncludedHasFries(addedProduct);
  const hasDrink = productIncludedHasDrink(addedProduct);
  const isBurger = productIsBurgerish(addedProduct);
  const lines = [];
  if (hasFries) {
    lines.push(isBurger ? 'Tu hamburguesa ya viene con papas 🍟' : 'Tu pedido ya viene con papas 🍟');
  }
  if (addonRows.length > 0) {
    lines.push(!hasDrink ? '¿Querés agregar bebida?' : '¿Querés sumar algo extra a lo que agregaste?');
  }
  let hook = lines.join('\n');
  if (externalUpsell.length > 0 && addonRows.length > 0) {
    hook += '\n\nTambién podés mirar esto:';
  } else if (externalUpsell.length > 0 && addonRows.length === 0) {
    if (hasFries && !hasDrink) {
      hook = isBurger
        ? 'Tu hamburguesa ya viene con papas 🍟\n¿Querés agregar bebida?\n\nMirá también esto 👇'
        : 'Tu pedido ya viene con papas 🍟\n¿Querés agregar bebida?\n\nMirá también esto 👇';
    } else {
      hook =
        idKey(addedProduct?.id).length % 2 === 0
          ? 'Esto combina perfecto con lo que elegiste 👇'
          : 'La mayoría le agrega esto 👇';
    }
  }
  return hook;
}

/**
 * @param {object} deps
 * @param {() => object|null} deps.getMenuData
 * @param {() => any[]} deps.getCartLinesForUpsell
 * @param {function} deps.openAssistantCheckout
 * @param {function} deps.showCheckoutPreferencePrompt
 * @param {function} deps.showCartSummaryInChat
 * @param {function} deps.handleMainChoice
 * @param {function} deps.continueShoppingFromAssistant
 * @param {function} deps.addBotMessage
 * @param {function} deps.buildAfterUpsellClosingText
 * @param {function} deps.trackClosingPromptShown
 */
export function createAssistantUpsellFlow(deps) {
  const {
    getMenuData,
    getCartLinesForUpsell,
    openAssistantCheckout,
    showCheckoutPreferencePrompt,
    showCartSummaryInChat,
    handleMainChoice,
    continueShoppingFromAssistant,
    addBotMessage,
    buildAfterUpsellClosingText,
    trackClosingPromptShown,
  } = deps;

  const upsellCtx = () => ({
    menuData: getMenuData(),
    getCartLinesForUpsell,
  });

  const cartOrMenuAfterAddOptions = () => [
    {
      label: 'Ver lo que llevás',
      value: 'view-cart-after-add',
      icon: ShoppingCart,
      action: () => showCartSummaryInChat(),
    },
    {
      label: 'Completa tus datos para terminar el pedido',
      value: 'checkout-chat-after-add',
      icon: MessageCircle,
      action: () => {
        if (typeof showCheckoutPreferencePrompt === 'function') {
          showCheckoutPreferencePrompt();
        } else {
          openAssistantCheckout();
        }
      },
    },
    {
      label: 'Agregar algo más',
      value: 'add-more-assistant',
      icon: Plus,
      action: () => handleMainChoice('order'),
    },
    {
      label: 'Seguir eligiendo',
      value: 'continue-assistant-chat',
      icon: Sparkles,
      action: () => continueShoppingFromAssistant(),
    },
  ];

  const showAfterAddActions = () => {
    setTimeout(() => {
      const t = buildAfterUpsellClosingText();
      trackClosingPromptShown();
      addBotMessage(t, cartOrMenuAfterAddOptions());
    }, 250);
  };

  const showUpsellAfterAdd = (addedProduct, cartLineId) => {
    const addonRows = buildPostAddAddonCardRows(addedProduct, cartLineId);
    const upsell = pickUpsellProducts(addedProduct, upsellCtx());
    if (addonRows.length === 0 && upsell.length === 0) {
      showAfterAddActions();
      return;
    }
    const hook = buildPostAddUpsellHook(addedProduct, addonRows, upsell);
    setTimeout(() => {
      const textAfter = buildAfterUpsellClosingText();
      trackClosingPromptShown();
      addBotMessage(hook, cartOrMenuAfterAddOptions(), upsell.length ? upsell : null, {
        skipUpsellForProductAdds: true,
        textAfterProducts: textAfter,
        postAddAddonCards: addonRows.length ? addonRows : undefined,
      });
    }, 280);
  };

  return {
    cartOrMenuAfterAddOptions,
    showUpsellAfterAdd,
    showAfterAddActions,
  };
}
